import React, { useEffect, useState, useCallback } from "react";
import { Cpu, Loader2, Search, ArrowUp, ArrowDown, RefreshCw } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import { Pagination } from "./Pagination";


interface KernelModulesProps {

    asset_id: string;
    client_id: string;

}

const KernelModules: React.FC<KernelModulesProps> = ({

    asset_id,
    client_id

}) => {
    const [moduleData, setModuleData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);

    // Search state
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchModules = useCallback(async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * limit;
            const queryObj: any = { asset_id, client_id };
            if (debouncedSearchTerm) {
                queryObj.$or = [
                    { name: { $ilike: `%${debouncedSearchTerm}%` } },
                    { used_by: { $ilike: `%${debouncedSearchTerm}%` } },
                    { status: { $ilike: `%${debouncedSearchTerm}%` } }
                ];
            }
            const params: any = {
                limit,
                skip,
                query: JSON.stringify(queryObj),
            };
            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }
            const response = await baseapi.get("/asset_kernel_modules", { params });

            if (response.data.status) {
                setModuleData(response.data.data ?? []);
                setTotalRecords(response.data.total ?? 0);
            } else {
                setModuleData([]);
                setTotalRecords(0);
            }
        } catch (err: any) {
            showError(err.message);
            setModuleData([]);
            setTotalRecords(0);
        }
        setLoading(false);
    }, [asset_id, currentPage, limit, debouncedSearchTerm, sortConfig]);

    useEffect(() => {
        fetchModules();
    }, [fetchModules]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
        setCurrentPage(1);
    };

    const SortIcon = ({ column }: { column: string }) => {
        if (sortConfig?.key !== column) {
            return (
                <div className="opacity-0 group-hover:opacity-40 transition-opacity">
                    <ArrowUp size={10} className="text-slate-400" />
                </div>
            );
        }
        return sortConfig.direction === 'asc'
            ? <ArrowUp size={10} className="text-orange-500" />
            : <ArrowDown size={10} className="text-orange-500" />;
    };

    // Calculate total pages for the Pagination component
    const totalPages = Math.max(1, Math.ceil(totalRecords / limit));

    return (
        <div className="p-3 h-full flex flex-col text-slate-900 dark:text-gray-200">
            {/* Compact Header */}
            <div className="flex items-center justify-between mb-3 border-b border-slate-200 dark:border-gray-700 pb-2 shrink-0">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5 shadow-sm">
                        <Cpu className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">Kernel Modules</h2>
                        <p className="text-slate-500 dark:text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                            {totalRecords || '...'} modules
                        </p>
                    </div>
                </div>

                {/* Search and Refresh */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={fetchModules}
                        disabled={loading}
                        className="p-2 bg-white dark:bg-gray-800 hover:bg-slate-100 dark:hover:bg-gray-700 text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                    </button>
                    <div className="relative w-64">
                        <Search className="w-3.5 h-3.5 text-slate-400 dark:text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            placeholder="Search kernel modules..."
                            className="w-64 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors h-8 shadow-sm"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* Table Area */}
            <div className="flex-1 overflow-hidden flex flex-col bg-white dark:bg-gray-900/40 border border-slate-200 dark:border-gray-700/50 rounded-md shadow-sm">
                <div className="overflow-x-auto custom-scroll flex-1">
                    <table className="w-full text-left text-xs text-slate-800 dark:text-gray-100 border-collapse min-w-[600px]">
                        <thead className="bg-slate-50 dark:bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-slate-200 dark:border-gray-700/50">
                                <th className="px-4 py-3 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap cursor-pointer group hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors" onClick={() => handleSort('name')}>
                                    <div className="flex items-center gap-1.5">
                                        Module Name
                                        <SortIcon column="name" />
                                    </div>
                                </th>
                                <th className="px-4 py-3 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap cursor-pointer group hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors" onClick={() => handleSort('status')}>
                                    <div className="flex items-center gap-1.5">
                                         Status
                                        <SortIcon column="status" />
                                    </div>
                                </th>
                                <th className="px-4 py-3 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap cursor-pointer group hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors" onClick={() => handleSort('used_by')}>
                                    <div className="flex items-center gap-1.5">
                                       Used By
                                        <SortIcon column="used_by" />
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-gray-700/60 bg-white/50 dark:bg-gray-900/20 text-xs">
                            {loading ? (
                                <tr>
                                    <td colSpan={3} className="px-4 py-8 text-center text-slate-500 dark:text-gray-500">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-orange-500" />
                                        Loading kernel modules...
                                    </td>
                                </tr>
                            ) : moduleData.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="px-4 py-8 text-center text-slate-500 dark:text-gray-500 opacity-60">
                                        <Cpu className="w-6 h-6 text-slate-400 dark:text-gray-700 mx-auto mb-2" />
                                        No Kernel Modules Found
                                    </td>
                                </tr>
                            ) : (
                                moduleData.map((mod) => (
                                    <tr key={mod.id} className="hover:bg-slate-50 dark:hover:bg-gray-800/40 transition-colors">
                                        <td className="px-4 py-2.5 font-mono text-[11px] font-medium text-slate-900 dark:text-white">
                                            {mod.name || "-"}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${mod.status?.toLowerCase() === 'live'
                                                ? 'bg-emerald-50 dark:bg-green-500/10 text-emerald-700 dark:text-green-400 border-emerald-200 dark:border-green-500/20'
                                                : 'bg-slate-100 dark:bg-gray-500/10 text-slate-600 dark:text-gray-400 border-slate-200 dark:border-gray-500/20'
                                                }`}>
                                                {mod.status || "-"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-[10px] text-slate-600 dark:text-gray-400 max-w-[300px]">
                                            {mod.used_by && mod.used_by !== "-" ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {mod.used_by.split(',').map((dep: string, i: number) => (
                                                        <span key={i} className="px-1.5 py-0.5 bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded text-slate-700 dark:text-gray-300 font-mono">
                                                            {dep.trim()}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-slate-400 dark:text-gray-500">-</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Integration of the Pagination Component */}
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    itemsPerPage={limit}
                    totalItems={totalRecords}
                    onItemsPerPageChange={(n) => {
                        setLimit(n);
                        setCurrentPage(1);
                    }}
                />
            </div>
        </div>
    );
};

export default KernelModules;